<script setup lang="ts">
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const active = ref<string>('0');
const collapsed = ref<boolean>(true);

const accordionData = computed(() => [
    {
        key: '0',
        label: t('project-configuration.pasteFromExcel.instructions.step1.heading'),
        content: t("project-configuration.pasteFromExcel.instructions.step1.content")
    },
    {
        key: '1',
        label: t('project-configuration.pasteFromExcel.instructions.step2.heading'),
        content: t('project-configuration.pasteFromExcel.instructions.step2.content'),
        isHtml: true
    },
    {
        key: '2',
        label: t('project-configuration.pasteFromExcel.instructions.step3.heading'),
        content: t("project-configuration.pasteFromExcel.instructions.step3.content")
    },
    {
        key: '3',
        label: t('project-configuration.pasteFromExcel.instructions.step4.heading'),
        content: t("project-configuration.pasteFromExcel.instructions.step4.content")
    },
    {
        key: '4',
        label: t('project-configuration.pasteFromExcel.instructions.step5.heading'),
        isCustomContent: true
    }
]);
</script>

<template>
    <Panel id="participant-excel-instructions-panel" toggleable v-model:collapsed="collapsed">
        <template #header>
            <div class="w-full cursor-pointer" @click="collapsed = !collapsed">
                <strong>{{ t("profile-overview.instructions.heading") }}</strong>
            </div>
        </template>
        <div class="flex flex-wrap gap-4">
            <div class="flex-1">
                <b>{{ t("project-configuration.pasteFromExcel.instructions.heading") }}</b>
            </div>
            <div class="flex-1">
                <div class="flex mb-4 gap-2 justify-end">
                    <Button id="excel-step-1-button" @click="active = '0'" rounded label="1" class="w-8 h-8 p-0" :outlined="active !== '0'" :aria-label="t('accessibility.buttons.step', {step: 1})" />
                    <Button id="excel-step-2-button" @click="active = '1'" rounded label="2" class="w-8 h-8 p-0" :outlined="active !== '1'" :aria-label="t('accessibility.buttons.step', {step: 2})" />
                    <Button id="excel-step-3-button" @click="active = '2'" rounded label="3" class="w-8 h-8 p-0" :outlined="active !== '2'" :aria-label="t('accessibility.buttons.step', {step: 3})" />
                    <Button id="excel-step-4-button" @click="active = '3'" rounded label="4" class="w-8 h-8 p-0" :outlined="active !== '3'" :aria-label="t('accessibility.buttons.step', {step: 4})" />
                    <Button id="excel-step-5-button" @click="active = '4'" rounded label="5" class="w-8 h-8 p-0" :outlined="active !== '4'" :aria-label="t('accessibility.buttons.step', {step: 5})" />
                </div>
            </div>
        </div>
        <Accordion v-model:value="active">
            <AccordionPanel v-for="(item, index) in accordionData" :key="item.key" :value="`${index}`" :id="`excel-step-${index + 1}-panel`">
                <AccordionHeader>{{ item.label }}</AccordionHeader>
                <AccordionContent>
                    <div v-if="item.isCustomContent && index === 4" class="flex flex-wrap gap-4 m-0">
                        <div class="basis-full mb-4">
                            {{ t("project-configuration.pasteFromExcel.instructions.step5.introduction") }}
                        </div>
                        <div class="flex-1">
                            <Panel :header="t('project-configuration.pasteFromExcel.instructions.step5.excel')">
                                <p class="monospaced mb-6">
                                    <span>Email | First Name | Last Name</span><br />
                                    <span>---------------------------------</span><br />
                                    <span>john@example.com | John, Jr. | Doe</span><br />
                                    <span>jane@example.com | Jane | Doe</span>
                                </p>
                                <p>{{ t("project-configuration.pasteFromExcel.instructions.step5.note") }}</p>
                            </Panel>
                        </div>
                        <div class="flex-1">
                            <Panel :header="t('project-configuration.pasteFromExcel.instructions.step5.application')">
                                <p class="monospaced">
                                    <span>john@example.com, "John, Jr.", Doe</span><br />
                                    <span>jane@example.com, Jane, Doe</span>
                                </p>
                                {{ t("project-configuration.pasteFromExcel.instructions.step5.separator") }}
                                <p class="monospaced">
                                    <span>john@example.com; "John, Jr."; Doe</span><br />
                                    <span>jane@example.com; Jane; Doe</span>
                                </p>
                                {{ t("project-configuration.pasteFromExcel.instructions.step5.separator") }}
                                <p class="monospaced">
                                    <span>john@example.com "John, Jr." Doe</span><br />
                                    <span>jane@example.com Jane Doe</span>
                                </p>
                            </Panel>
                        </div>
                    </div>
                    <p v-else-if="item.isHtml" class="m-0" v-html="item.content"></p>
                    <p v-else class="m-0">{{ item.content }}</p>
                </AccordionContent>
            </AccordionPanel>
        </Accordion>
    </Panel>
</template>

<style>
.no-wrap {
    white-space: nowrap;
}
.monospaced {
    font-family: monospace;
}
</style>
